import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Loader2, Mail, Bell, BellOff, Send, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQUENCY_LABELS = {
  daily: { label: 'Daily', desc: 'Every evening — only if new events matched your filters' },
  every_2_days: { label: 'Every 2 days', desc: 'A lighter drumbeat, still fresh' },
  weekly: { label: 'Weekly', desc: 'Sunday-evening rundown of the week ahead' },
  off: { label: 'Off', desc: 'Pause the event digest completely' },
};

const SPORT_LABELS = {
  roller_skate: 'Rollerskating',
  inline_skate: 'Inline Skates',
  skateboard: 'Skateboard',
  longboard: 'Longboard',
  bmx: 'BMX',
  scooter: 'Scooter',
  snow_sports: 'Snow Sports',
};

export const EmailPrefsCard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [dirty, setDirty] = useState(false);

  const token = useMemo(
    () => localStorage.getItem('rollm8s_token') || localStorage.getItem('token'),
    [],
  );
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/users/email-prefs`, { headers });
        // Deep-link support: `?add_sport=snow_sports` pre-selects the sport.
        const addSport = searchParams.get('add_sport');
        if (addSport && data.available_sports?.includes(addSport)) {
          if (!data.digest_sports.includes(addSport)) {
            data.digest_sports = [...data.digest_sports, addSport];
            setDirty(true);
            toast.success(`${SPORT_LABELS[addSport] || addSport} added — tap Save to confirm`);
          }
          // Strip the query param so a refresh doesn't re-toast.
          const next = new URLSearchParams(searchParams);
          next.delete('add_sport');
          setSearchParams(next, { replace: true });
        }
        setPrefs(data);
      } catch (err) {
        toast.error('Failed to load email preferences');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setPref = (patch) => {
    setPrefs((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  };

  const toggleSport = (sport) => {
    const current = new Set(prefs.digest_sports || []);
    if (current.has(sport)) current.delete(sport);
    else current.add(sport);
    setPref({ digest_sports: Array.from(current) });
  };

  const handleSave = async () => {
    if (!prefs) return;
    setSaving(true);
    try {
      const payload = {
        digest_frequency: prefs.digest_frequency,
        digest_sports: prefs.digest_sports,
        digest_country_filter: prefs.digest_country_filter,
        opted_out: prefs.opted_out,
      };
      const { data } = await axios.put(`${API}/users/email-prefs`, payload, { headers });
      setPrefs(data);
      setDirty(false);
      toast.success('Notification preferences saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSendPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await axios.post(`${API}/users/email-prefs/preview`, {}, { headers });
      if (data.sent > 0) {
        toast.success('Preview digest sent — check your inbox');
      } else if (data.skipped_no_events > 0) {
        toast.info('No new events matched your filters right now — nothing to send');
      } else {
        toast.info('No digest sent (check your prefs)');
      }
    } catch (err) {
      toast.error('Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return (
      <Card className="rounded-2xl bg-card/60 border-border/40">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!prefs) return null;

  const isPaused = prefs.opted_out || prefs.digest_frequency === 'off';

  return (
    <div className="space-y-4" data-testid="email-prefs-card">
      <Card className="rounded-2xl bg-gradient-to-br from-fuchsia-500/10 to-purple-500/5 border-fuchsia-400/30">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-fuchsia-400/20 flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 text-fuchsia-300" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-base">New events digest</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Get an email whenever new events in your chosen sports drop. We only send when there&apos;s something fresh — quiet days stay quiet.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Master pause */}
      <Card className="rounded-2xl bg-card/60 border-border/40">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              {isPaused ? (
                <BellOff className="w-5 h-5 text-muted-foreground flex-shrink-0" />
              ) : (
                <Bell className="w-5 h-5 text-fuchsia-400 flex-shrink-0" />
              )}
              <div className="min-w-0">
                <p className="font-medium text-sm">Digest emails</p>
                <p className="text-xs text-muted-foreground">
                  {isPaused ? 'Currently paused' : 'Currently active'}
                </p>
              </div>
            </div>
            <Switch
              checked={!prefs.opted_out}
              onCheckedChange={(v) => setPref({ opted_out: !v })}
              data-testid="digest-master-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Frequency picker */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-1">
          How often
        </p>
        <div className="space-y-2">
          {Object.entries(FREQUENCY_LABELS).map(([value, meta]) => {
            const selected = prefs.digest_frequency === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => setPref({ digest_frequency: value })}
                data-testid={`freq-${value}`}
                disabled={prefs.opted_out && value !== 'off'}
                className={`w-full text-left p-3 rounded-xl border-2 transition-all ${
                  selected
                    ? 'border-fuchsia-400 bg-fuchsia-400/10'
                    : 'border-border/40 bg-card/40 hover:border-fuchsia-400/40'
                } ${prefs.opted_out && value !== 'off' ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-sm">{meta.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{meta.desc}</p>
                  </div>
                  {selected && (
                    <div className="w-6 h-6 rounded-full bg-fuchsia-400 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3.5 h-3.5 text-black" />
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Sports */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-1">
          Which sports?
        </p>
        <Card className="rounded-2xl bg-card/60 border-border/40">
          <CardContent className="p-4 space-y-2">
            {(prefs.available_sports || []).map((sport) => {
              const active = (prefs.digest_sports || []).includes(sport);
              return (
                <button
                  key={sport}
                  type="button"
                  onClick={() => toggleSport(sport)}
                  data-testid={`sport-${sport}`}
                  className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                    active ? 'bg-fuchsia-400/10 border border-fuchsia-400/40' : 'bg-transparent hover:bg-white/5 border border-transparent'
                  }`}
                >
                  <span className="text-sm font-medium">{SPORT_LABELS[sport] || sport}</span>
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                      active ? 'bg-fuchsia-400 border-fuchsia-400' : 'border-border'
                    }`}
                  >
                    {active && <Check className="w-3 h-3 text-black" />}
                  </div>
                </button>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Country filter */}
      <Card className="rounded-2xl bg-card/60 border-border/40">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="font-medium text-sm">Only my country</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Filter events by the country on your profile. Turn off to see globally.
              </p>
            </div>
            <Switch
              checked={!!prefs.digest_country_filter}
              onCheckedChange={(v) => setPref({ digest_country_filter: v })}
              data-testid="country-filter-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Save + preview */}
      <div className="flex flex-col sm:flex-row gap-3 pt-2">
        <Button
          onClick={handleSave}
          disabled={!dirty || saving}
          className="flex-1 rounded-full h-11"
          data-testid="save-prefs-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save changes'}
        </Button>
        <Button
          onClick={handleSendPreview}
          disabled={previewing || dirty}
          variant="outline"
          className="flex-1 rounded-full h-11"
          data-testid="preview-digest-btn"
        >
          {previewing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
          Send me a preview
        </Button>
      </div>
      {dirty && (
        <p className="text-xs text-amber-400 text-center">Unsaved changes — hit Save to lock them in</p>
      )}
    </div>
  );
};

export default EmailPrefsCard;
